/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import useSubmissionPreview from './useSubmissionPreview';
import {
  SubmissionPreviewActions,
  SubmissionPreviewCanvas,
  SubmissionPreviewDialogs,
  PreviewFilesSelect,
  SubmissionPreviewFooter,
  PreviewHeader,
  SidebarAttachmentCards,
  SubmissionPreviewFixedCards,
} from '../../components/preview';
import SubmissionStatusChangeDialog from '../../components/status/SubmissionStatusChangeDialog';
import SurveyDialog from './SurveyDialog';
import Breadcrumb from '../../components/common/Breadcrumb';
import { getSubmissionTitle } from '../../shared/submission/submissionPropertyAccessor';
import { useUiSchema } from '../../shared/rjsf/useUiSchema';
import { RoutePath } from '../../shared/enums/RoutePath';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateRows: 'auto auto auto auto auto auto ',
    gridTemplateAreas:
      '' +
      '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
      '"header header header header"' +
      '"staticText staticText staticText staticText"' +
      '"actions actions actions actions"' +
      '"select select select select"' +
      '"canvas canvas canvas canvas"',
    margin: '1.5rem 0',
    padding: '0 1rem',
    minHeight: 'calc(100vh - 10.1875rem)', // 100% of viewport - (header + footer)
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas:
        '' +
        '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
        '"header header header header"' +
        '"staticText staticText staticText staticText"' +
        '". . . ."' +
        '"select select actions actions"' +
        '"canvas canvas canvas canvas"',
      padding: '0 1.5rem',
    },
    [theme.breakpoints.up('lg')]: {
      minHeight: 'calc(100vh - 11.1875rem)',
      margin: '2rem 0',
      marginLeft: ({ isRestricted }) => (isRestricted ? '3rem' : '8.75rem'),
      paddingRight: '1.5rem',
    },
    [theme.breakpoints.up('xl2')]: {
      gridTemplateAreas:
        '' +
        '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
        '"header header header header"' +
        '"staticText staticText actions actions"' +
        '". . . ."' +
        '". . . ."' +
        '"sidebar canvas canvas canvas"',
      padding: '0',
      paddingRight: '2rem',
      marginLeft: ({ isRestricted }) => (isRestricted ? '3rem' : '8.5rem'),
    },
  },
  breadcrumbs: {
    gridArea: 'breadcrumbs',
  },
  header: {
    gridArea: 'header',
    margin: '1rem 0',
    [theme.breakpoints.up('xl2')]: {
      margin: '2rem 0',
    },
  },
  staticText: {
    gridArea: 'staticText',
    marginBottom: '1rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: '2.5rem',
    },
  },
  actions: {
    gridArea: 'actions',

    [theme.breakpoints.up('md')]: {
      justifySelf: 'flex-end',
    },

    marginBottom: '1.5rem',
    alignSelf: 'flex-end',
    [theme.breakpoints.up('xl2')]: {
      marginBottom: '2.5rem',
      alignSelf: 'flex-start',
    },
  },
  sidebar: {
    gridArea: 'sidebar',
  },
  select: {
    gridArea: 'select',
    marginBottom: '1.5rem',
  },
  canvas: {
    gridArea: 'canvas',
  },
  alert: {
    backgroundColor: '#ea8484',
  },
}));

export default function SubmissionPreview() {
  const location = useLocation();
  const { uiSchema } = useUiSchema();
  const {
    submission,
    topEntries,
    previewObject,
    signatureImageUrl,
    staticText,
    bodyButtonsConfig,
    footerButtonsConfig,
    loadSignature,
    loadSubmissionFile,
    loadAttachmentFile,
    dialogSendSubmission,
    dialogPreviewFacsimile,
    dialogStatusChange,
    dialogSurveySubmission,
    handleFacsimileImageError,
    t,
    attachments,
    attachmentsReady,
    isLoading,
    isVersionCorrect,
  } = useSubmissionPreview();

  const isMediumOrSmaller = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isExtraLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('xl2'));
  const classes = useStyles({ isRestricted: RoutePath.isUmaRoute(location?.pathname) });

  return (
    attachmentsReady &&
    submission && (
      <>
        <div className={classes.container}>
          <Breadcrumb
            className={classes.breadcrumbs}
            currentBreadcrumb={t('Preview.title', { title: getSubmissionTitle(submission, uiSchema) })}
          />
          {!isVersionCorrect && (
            <Typography variant="h4" className={classes.alert}>
              <strong>{t('submission.incompatible.version')}</strong>
            </Typography>
          )}

          <PreviewHeader className={classes.header} submission={submission} headerTitle={t('submission.preview')} />
          <Typography className={classes.staticText}>{staticText}</Typography>
          <SubmissionPreviewActions className={classes.actions} buttonsConfig={bodyButtonsConfig} />
          {isExtraLargeOrBigger ? (
            <div className={classes.sidebar}>
              <SubmissionPreviewFixedCards
                submission={submission}
                previewObject={previewObject}
                loadSignature={loadSignature}
                loadSubmissionFile={loadSubmissionFile}
              />
              <SidebarAttachmentCards
                attachments={attachments}
                previewObject={previewObject}
                loadAttachmentFile={loadAttachmentFile}
              />
            </div>
          ) : (
            <PreviewFilesSelect
              className={classes.select}
              submission={submission}
              topEntries={topEntries}
              attachments={attachments}
              previewObject={previewObject}
              loadSignature={loadSignature}
              loadSubmissionFile={loadSubmissionFile}
              loadAttachmentFile={loadAttachmentFile}
            />
          )}

          <SubmissionPreviewCanvas className={classes.canvas} submission={submission} previewObject={previewObject} />

          <SubmissionPreviewDialogs
            submission={submission}
            signatureImageUrl={signatureImageUrl}
            dialogSendSubmission={dialogSendSubmission}
            dialogPreviewFacsimile={dialogPreviewFacsimile}
            handleFacsimileImageError={handleFacsimileImageError}
            isLoading={isLoading}
          />
        </div>

        {isMediumOrSmaller && (
          <SubmissionPreviewFooter className={classes.footer} buttonsConfig={footerButtonsConfig} />
        )}

        {submission && (
          <SubmissionStatusChangeDialog
            open={dialogStatusChange.isOpen}
            handleConfirm={dialogStatusChange.confirm}
            handleCancel={dialogStatusChange.cancel}
            submission={submission}
            revertStatusAction={dialogStatusChange.revertStatusAction}
          />
        )}

        {submission && (
          <SurveyDialog
            dialogSurveySubmission={dialogSurveySubmission}
            open={dialogSurveySubmission.isOpen}
            handleConfirm={dialogSurveySubmission.openDialogSurveySubmission}
            handleCancel={dialogSurveySubmission.closeDialogSurveySubmission}
            submission={submission}
            revertStatusAction={dialogStatusChange.revertStatusAction}
          />
        )}
      </>
    )
  );
}
